import React from 'react';

import { BrandGuidelinesBody } from './bodies/BrandGuidelinesBody';
import { ConceptDesignBody } from './bodies/ConceptDesignBody';
import { DocumentBody } from './bodies/DocumentBody';
import { ImageBody } from './bodies/ImageBody';
import { PostBody } from './bodies/PostBody';
import { RepurposeBody } from './bodies/RepurposeBody';
import { StrategyBody } from './bodies/StrategyBody';
import { VideoBody } from './bodies/VideoBody';
import { WorkflowBriefCaptureBody } from './bodies/WorkflowBriefCaptureBody';
import { WorkflowTrackerBody } from './bodies/WorkflowTrackerBody';
import type { Subject } from './types';

import { VoicePreviewCard } from '@/components/brand-voice/VoicePreviewCard';
import type { InspectorBreadcrumbItem } from '@/components/ui/inspector/types';

interface InspectorBodyProps {
  subject: Subject;
  onClose: () => void;
  breadcrumbs?: InspectorBreadcrumbItem[];
  studioPanel?: React.ReactNode;
  backToProject?: { id: string; name: string; onReturn: () => void };
}

/**
 * Body dispatcher — picks a body component based on subject.kind.
 * Exhaustiveness is enforced via the `never` branch — adding a new kind
 * to Subject without a matching case is a TS error.
 */
export const InspectorBody: React.FC<InspectorBodyProps> = ({
  subject,
  onClose,
  breadcrumbs,
  studioPanel,
  backToProject,
}) => {
  switch (subject.kind) {
    case 'post':
      return (
        <PostBody
          postId={subject.postId}
          targetId={subject.targetId}
          onClose={onClose}
          breadcrumbs={breadcrumbs}
          studioPanel={studioPanel}
          backToProject={backToProject}
        />
      );
    case 'image':
      void backToProject;
      return (
        <ImageBody
          mediaId={subject.mediaId}
          sourcePostId={subject.sourcePostId}
          onClose={onClose}
          breadcrumbs={breadcrumbs}
        />
      );
    case 'video':
      return (
        <VideoBody
          mediaId={subject.mediaId}
          jobId={subject.jobId}
          onClose={onClose}
          breadcrumbs={breadcrumbs}
          backToProject={backToProject}
        />
      );
    case 'document':
      return (
        <DocumentBody messageId={subject.messageId} onClose={onClose} breadcrumbs={breadcrumbs} />
      );
    case 'repurpose':
      return (
        <RepurposeBody
          sourcePostId={subject.sourcePostId}
          onClose={onClose}
          backToProject={backToProject}
        />
      );
    case 'concept_design':
      return (
        <ConceptDesignBody
          briefId={subject.briefId}
          projectTaskId={subject.projectTaskId}
          onClose={onClose}
        />
      );
    case 'workflow_tracker':
      return <WorkflowTrackerBody projectId={subject.projectId} onClose={onClose} />;
    case 'strategy':
      return (
        <StrategyBody projectId={subject.projectId} onClose={onClose} breadcrumbs={breadcrumbs} />
      );
    case 'brand_guidelines':
      return (
        <BrandGuidelinesBody
          brandKitId={subject.brandKitId}
          onClose={onClose}
          breadcrumbs={breadcrumbs}
        />
      );
    case 'voice-preview':
      return (
        <div className="p-5">
          <VoicePreviewCard
            draft={subject.draft}
            withVoice={subject.withVoice}
            withoutVoice={subject.withoutVoice}
            contentType={subject.contentType}
            onSave={subject.onSave}
            onEdit={subject.onEdit}
          />
        </div>
      );
    case 'workflow-brief-capture':
      return (
        <WorkflowBriefCaptureBody
          projectId={subject.projectId}
          missingKeys={subject.missingKeys}
          onSubmitted={subject.onSubmitted}
        />
      );
    default: {
      const _exhaustive: never = subject;
      void _exhaustive;
      return null;
    }
  }
};
